import { Bell, Search, Moon, Sun, Eye, Folder, AlertCircle, Menu, PanelLeft } from 'lucide-react'
import { useTheme } from '@/providers/theme-provider'
import { useSidebar } from '@/providers/sidebar-provider'
import { BranchSelector } from '@/components/dashboard/branch-selector'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'
import * as React from 'react'

interface HeaderProps {
  title?: string
  subtitle?: string
  children?: React.ReactNode
}

export function Header({ title = 'Hello, Admin', subtitle = 'Welcome to NurinV2 Healthcare Analytics', children }: HeaderProps) {
  const { setTheme, resolvedTheme } = useTheme()
  const { setOpenMobile } = useSidebar()
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false)

  return (
    <header className="py-4 px-4 sm:py-5 sm:px-6 lg:py-7 lg:px-9">
      {/* Desktop Layout - Single Row */}
      <div className="hidden lg:flex items-start justify-between">
        {/* Left - Greeting */}
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-[26px] font-semibold text-text-dark tracking-tight">
              {title}
            </h1>
            {children}
          </div>
          <p className="text-[15px] text-text-secondary font-normal">
            {subtitle}
          </p>
        </div>

        {/* Right - Actions */}
        <div className="flex items-center gap-3">
          {/* Search Box */}
          <div className="flex items-center gap-2.5 bg-card rounded-full px-4 py-2.5 w-[200px] border border-border transition-all focus-within:ring-2 focus-within:ring-info/20">
            <Search className="w-[18px] h-[18px] text-foreground flex-shrink-0" />
            <input
              type="text"
              placeholder="Search"
              className="border-none outline-none text-[14px] font-normal w-full bg-transparent text-text-dark placeholder:text-text-muted"
            />
          </div>

          {/* Icon Buttons */}
          <div className="flex items-center gap-1">
            <button className="w-10 h-10 rounded-md border-none bg-transparent cursor-pointer flex items-center justify-center transition-colors hover:bg-hover">
              <Eye className="w-5 h-5 text-foreground" />
            </button>
            <button className="w-10 h-10 rounded-md border-none bg-transparent cursor-pointer flex items-center justify-center transition-colors hover:bg-hover">
              <Folder className="w-5 h-5 text-foreground" />
            </button>
            <button className="w-10 h-10 rounded-md border-none bg-transparent cursor-pointer flex items-center justify-center transition-colors hover:bg-hover">
              <AlertCircle className="w-5 h-5 text-foreground" />
            </button>
          </div>

          {/* Divider */}
          <div className="w-px h-6 bg-border mx-1.5" />

          {/* Branch Selector */}
          <BranchSelector />

          {/* Theme Toggle */}
          <button
            className="w-10 h-10 rounded-md border-none bg-transparent cursor-pointer flex items-center justify-center transition-colors hover:bg-hover"
            onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
          >
            {resolvedTheme === 'dark' ? (
              <Sun className="w-5 h-5 text-foreground" />
            ) : (
              <Moon className="w-5 h-5 text-foreground" />
            )}
          </button>

          {/* Notifications */}
          <button className="w-10 h-10 rounded-md border-none bg-transparent cursor-pointer flex items-center justify-center transition-colors hover:bg-hover">
            <Bell className="w-5 h-5 text-foreground" />
          </button>
        </div>
      </div>

      {/* Mobile Layout - Two Rows */}
      <div className="lg:hidden flex flex-col gap-3">
        {/* Row 1: Hamburger + Title + Theme + Menu */}
        <div className="flex items-center justify-between">
          {/* Left: Hamburger + Title */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setOpenMobile(true)}
              className="w-9 h-9 rounded-md bg-transparent flex items-center justify-center transition-colors hover:bg-hover"
              aria-label="Open navigation menu"
            >
              <PanelLeft className="w-5 h-5 text-foreground" />
            </button>
            <div>
              <h1 className="text-lg font-semibold text-text-dark tracking-tight">
                {title}
              </h1>
            </div>
          </div>

          {/* Right: Theme + Menu */}
          <div className="flex items-center gap-1">
            <button
              className="w-9 h-9 rounded-md bg-transparent flex items-center justify-center transition-colors hover:bg-hover"
              onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
            >
              {resolvedTheme === 'dark' ? (
                <Sun className="w-5 h-5 text-foreground" />
              ) : (
                <Moon className="w-5 h-5 text-foreground" />
              )}
            </button>

            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <button className="w-9 h-9 rounded-md bg-transparent flex items-center justify-center transition-colors hover:bg-hover">
                  <Menu className="w-5 h-5 text-foreground" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[280px] p-4">
                <div className="flex flex-col gap-4 mt-4">
                  <div className="flex items-center gap-2.5 bg-muted rounded-lg px-3 py-2.5 border border-border">
                    <Search className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <input
                      type="text"
                      placeholder="Search..."
                      className="border-none outline-none text-sm w-full bg-transparent"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <button className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted transition-colors text-left">
                      <Eye className="w-5 h-5" />
                      <span className="text-sm">View Mode</span>
                    </button>
                    <button className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted transition-colors text-left">
                      <Folder className="w-5 h-5" />
                      <span className="text-sm">Files</span>
                    </button>
                    <button className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted transition-colors text-left">
                      <AlertCircle className="w-5 h-5" />
                      <span className="text-sm">Alerts</span>
                    </button>
                    <button className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted transition-colors text-left">
                      <Bell className="w-5 h-5" />
                      <span className="text-sm">Notifications</span>
                    </button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {/* Row 2: Subtitle + Branch Selector */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-text-secondary font-normal truncate flex-1 mr-2">
            {subtitle}
          </p>
          <BranchSelector />
        </div>

        {/* Row 3: Children (API status etc) if present */}
        {children && (
          <div className="flex items-center">
            {children}
          </div>
        )}
      </div>
    </header>
  )
}
